<template>
  <div class="appeal-review-page">
    <!-- 顶部标题栏 -->
    <div class="review-header">
      <div class="header-left">
        <el-button icon="el-icon-back" @click="goBack">返回</el-button>
        <span class="title">申诉审核 - {{ batchName }}</span>
      </div>
      <div class="header-center">
        <span class="stats">
          总计 <strong>{{ totalCount }}</strong> 份 | 
          待审核 <strong>{{ pendingCount }}</strong> | 
          已完成 <strong>{{ completedCount }}</strong>
        </span>
      </div>
      <div class="header-right">
        <!-- 预留右侧区域 -->
      </div>
    </div>

    <!-- 三栏布局 -->
    <div class="review-content">
      <!-- 左侧：店铺列表 -->
      <div class="left-panel">
        <div class="panel-header">
          <div class="panel-title">门店导航</div>
          <div class="panel-subtitle">当前共有 <strong>{{ pendingCount }}</strong> 个待处理项目</div>
        </div>
        
        <div class="search-box">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索门店名称或编号" 
            prefix-icon="el-icon-search"
            size="small"
            clearable
            @keyup.enter.native="filterRecords"
            @clear="filterRecords"
          ></el-input>
        </div>
        
        <div class="record-list">
          <div
            v-for="(record, index) in filteredRecords"
            :key="record.id"
            class="record-item"
            :class="{ 'is-current': currentRecord && currentRecord.id === record.id }"
            @click="selectRecord(record)"
          >
            <div class="record-icon" :class="{ 'is-current': currentRecord && currentRecord.id === record.id }">
              <i class="el-icon-office-building"></i>
            </div>
            <div class="record-info-main">
              <div class="record-name">{{ record.dealerName || ('店铺' + (index + 1)) }}</div>
              <div class="record-code">{{ record.shortDealerCode || record.dealerCode || '-' }}</div>
            </div>
            <span class="status-badge" :class="getRecordStatusClass(record)">
              {{ getRecordStatusText(record) }}
            </span>
          </div>
        </div>
        
        <div class="filter-option">
          <el-select v-model="statusFilter" size="mini" placeholder="筛选状态" clearable style="width: 100%;" @change="filterRecords">
            <el-option label="全部" value="" />
            <el-option label="待审核" value="pending" />
            <el-option label="已完成" value="completed" />
          </el-select>
        </div>
      </div>

      <!-- 中间：申诉详情与审核 -->
      <div class="center-panel">
        <!-- 门店级证据（从阶段 1/2/4 引入，挂门店不挂指标）-->
        <div
          v-if="currentRecord && currentDealerLevelEvidence && currentDealerLevelEvidence.length > 0"
          class="dealer-level-evidence-card">
          <div class="dealer-level-evidence-card-header">
            <i class="el-icon-paperclip"></i>
            <span>门店级证据</span>
            <el-tag size="mini" type="warning" effect="plain">{{ currentDealerLevelEvidence.length }} 个文件</el-tag>
            <span class="dealer-level-evidence-card-hint">来自阶段问卷，与具体指标无关，供参考</span>
          </div>
          <div class="dealer-level-evidence-card-list">
            <div
              v-for="(ev, idx) in currentDealerLevelEvidence"
              :key="idx"
              class="dealer-level-evidence-card-item"
              @click="openDealerLevelEvidence(ev)">
              <i :class="dealerEvIconClass(ev.fileType)" :style="{ color: dealerEvIconColor(ev.fileType) }"></i>
              <div class="dealer-level-evidence-card-info">
                <div class="dealer-level-evidence-card-name">{{ ev.displayName }}</div>
                <div class="dealer-level-evidence-card-meta" v-if="ev.sourceItemLabel">
                  <span v-if="ev.sourceSerialNo">{{ ev.sourceSerialNo }}</span>
                  <span v-if="ev.sourceItemLabel">· {{ ev.sourceItemLabel }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="currentRecord && currentItems.length > 0" class="appeal-list">
          <div
            v-for="(item, qIndex) in currentItems"
            :key="item.id"
            :ref="'item-' + qIndex"
            class="appeal-card"
          >
            <div class="card-header">
              <div class="card-title-row">
                <span class="item-serial" v-if="item.serialNo">{{ item.serialNo }}. </span><span class="item-title-text" v-html="item.itemLabel"></span>
              </div>
              <el-tag :type="getItemStatusType(item.reviewStatus)" size="mini" effect="plain">
                {{ getItemStatusText(item.reviewStatus) }}
              </el-tag>
            </div>
            
            <div class="card-body">
              <div class="process-section">
                <div class="process-section-title">原始审核信息</div>
              <!-- 下发申诉结果（原始审核结果快照） -->
              <div class="answer-section">
                <div class="section-header">
                  <i class="el-icon-document-checked"></i>
                  <span>下发申诉结果</span>
                </div>
                <div class="answer-options" v-if="item.originalAnswer || item.optionsJson || item.allOptions">
                  <div 
                    v-for="(opt, optIdx) in getOriginalOrCurrentOptions(item)" 
                    :key="optIdx"
                    class="answer-option-item"
                  >
                    <span class="option-text" v-html="opt.label"></span>
                    <el-tag v-if="opt.score > 0" type="success" size="mini">+{{ opt.score }}分</el-tag>
                  </div>
                </div>
                <div class="option-hint" v-else>
                  <i class="el-icon-info"></i> 暂无下发选项数据
                </div>
                <div class="review-remark-area" style="margin-top: 12px;">
                  <div class="remark-label">审核说明</div>
                  <div class="value-text">{{ item.originalReviewRemark || '-' }}</div>
                </div>
              </div>
              </div>
              
              <div class="process-section">
                <div class="process-section-title">门店申诉信息</div>
              <!-- 门店申诉陈述 -->
              <div class="appeal-statement-section">
                <div class="section-header">
                  <i class="el-icon-chat-line-square"></i>
                  <span>门店申诉陈述</span>
                </div>
                <div class="statement-quote">
                  <div class="quote-mark">"</div>
                  <div class="quote-text">{{ item.appealRemark || '无申诉说明' }}</div>
                </div>
              </div>
              
              <!-- 佐证材料 -->
              <div class="evidence-section" v-if="item.evidenceUrls">
                <div class="section-header">
                  <i class="el-icon-link"></i>
                  <span>申诉佐证材料</span>
                </div>
                <div class="evidence-cards" v-viewer="{movable: false}">
                  <template v-for="(file, idx) in parseEvidenceUrls(item.evidenceUrls)">
                    <div v-if="isImage(file)" :key="idx" class="evidence-card" @click.stop>
                      <div class="evidence-card-icon image">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                      <div class="evidence-card-info">
                        <div class="evidence-card-name">{{ file.name || '图片' }}</div>
                      </div>
                      <img :src="file.url" style="display:none" />
                    </div>
                    <div v-else :key="'f-'+idx" class="evidence-card" @click="previewFile(file)">
                      <div class="evidence-card-icon" :class="isVideo(file) ? 'video' : 'doc'">
                        <i :class="isVideo(file) ? 'el-icon-video-camera' : 'el-icon-document'"></i>
                      </div>
                      <div class="evidence-card-info">
                        <div class="evidence-card-name">{{ file.name || '附件' }}</div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              </div>

              <div class="process-section">
                <div class="process-section-title">申诉处理结果</div>
              <!-- 审核裁定 -->
              <div class="review-action-section" v-if="item.reviewStatus === 0">
                <div class="section-header">
                  <span>审核裁定</span>
                </div>
                <div class="verdict-cards">
                  <div 
                    class="verdict-card" 
                    :class="{ selected: item.pendingStatus === 1 }"
                    @click="item.pendingStatus = 1"
                  >
                    <i class="el-icon-close"></i>
                    <span>维持原判</span>
                  </div>
                  <div 
                    class="verdict-card" 
                    :class="{ selected: item.pendingStatus === 2 }"
                    @click="item.pendingStatus = 2"
                  >
                    <i class="el-icon-edit"></i>
                    <span>修改失分原因</span>
                  </div>
                  <div 
                    class="verdict-card" 
                    :class="{ selected: item.pendingStatus === 3 }"
                    @click="item.pendingStatus = 3"
                  >
                    <i class="el-icon-refresh-right"></i>
                    <span>还分处理</span>
                  </div>
                  <div 
                    class="verdict-card" 
                    :class="{ selected: item.pendingStatus === 4 }"
                    @click="item.pendingStatus = 4"
                  >
                    <i class="el-icon-remove-outline"></i>
                    <span>不涉及</span>
                  </div>
                </div>
                
                <!-- 修改失分原因：显示非答案选项供勾选 -->
                <div class="option-select-area" v-if="item.pendingStatus === 2 && item.allOptions && item.allOptions.length > 0">
                  <div class="option-select-label">选择选项：</div>
                  <div class="option-checkboxes">
                    <el-checkbox-group v-model="item.selectedOptionsInput">
                      <template v-for="opt in item.allOptions">
                        <el-checkbox 
                          v-if="!opt.isAnswer" 
                          :key="opt.option_key || opt.value" 
                          :label="opt.value"
                          class="option-checkbox"
                        >
                          <span v-html="opt.label"></span>
                        </el-checkbox>
                      </template>
                    </el-checkbox-group>
                    <div class="option-hint" v-if="getSelectableOptions(item, 2).length === 0">
                      <i class="el-icon-info"></i> 无可选择的非答案选项
                    </div>
                  </div>
                </div>
                
                <!-- 还分处理：显示答案选项供勾选 -->
                <div class="option-select-area" v-if="item.pendingStatus === 3 && item.allOptions && item.allOptions.length > 0">
                  <div class="option-select-label">还分选项：</div>
                  <div class="option-checkboxes">
                    <el-checkbox-group v-model="item.selectedOptionsInput" @change="calcRestoredScore(item)">
                      <template v-for="opt in item.allOptions">
                        <el-checkbox 
                          v-if="opt.isAnswer || opt.hasScore" 
                          :key="opt.option_key || opt.value" 
                          :label="opt.value"
                          class="option-checkbox scored"
                        >
                          <span v-html="opt.label"></span>
                          <el-tag v-if="opt.score > 0" type="success" size="mini">+{{ opt.score }}分</el-tag>
                          <el-tag v-if="opt.isAnswer" type="warning" size="mini">答案</el-tag>
                        </el-checkbox>
                      </template>
                    </el-checkbox-group>
                    <div class="option-hint" v-if="getSelectableOptions(item, 3).length === 0">
                      <i class="el-icon-info"></i> 无可选择的答案/计分选项
                    </div>
                  </div>
                </div>
                
                <!-- 还分时显示计算出的还分 -->
                <div class="restored-score-row" v-if="item.pendingStatus === 3">
                  <span class="restored-label">还分：</span>
                  <el-input-number 
                    v-model="item.restoredScoreInput" 
                    :min="0" 
                    :max="item.originalScore || 10"
                    size="small"
                    controls-position="right"
                  ></el-input-number>
                  <span class="score-hint">分 (根据选项自动计算，可手动调整)</span>
                </div>

                
                <!-- 审核备注 -->
                <div class="review-remark-area">
                  <div class="remark-label">审核备注</div>
                  <el-input 
                    v-model="item.reviewRemarkInput" 
                    type="textarea"
                    :rows="3"
                    placeholder="请输入您的复核意见与判定理由..."
                  ></el-input>
                </div>
                
                <!-- 审核佐证上传 -->
                <div class="review-evidence-area">
                  <div class="remark-label">审核佐证材料（可选）</div>
                  <el-upload
                    :action="uploadUrl"
                    :on-success="(res, file, fileList) => onReviewEvidenceUploaded(res, file, fileList, item)"
                    :on-remove="(file, fileList) => onReviewEvidenceRemoved(file, fileList, item)"
                    :file-list="item.reviewEvidenceFileList || []"
                    :limit="5"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    list-type="text"
                  >
                    <el-button size="small" type="text" icon="el-icon-upload2">上传佐证文件</el-button>
                    <div slot="tip" class="el-upload__tip">支持图片、PDF、Word，最多5个文件</div>
                  </el-upload>
                </div>
                
                <div class="submit-row">
                  <el-button 
                    type="primary" 
                    class="submit-btn"
                    :disabled="!item.pendingStatus"
                    @click="submitItemReview(item)"
                  >
                    提交审核结果
                  </el-button>
                </div>
              </div>
              
              <!-- 已审核结果 -->
              <div class="reviewed-result" v-else>
                <div class="result-card" :class="getReviewStatusStyle(item.reviewStatus)">
                  <i :class="item.reviewStatus === 3 ? 'el-icon-success' : 'el-icon-info'"></i>
                  <span>{{ getItemStatusText(item.reviewStatus) }}</span>
                  <span v-if="item.restoredScore" class="restored-score">（还分 +{{ item.restoredScore }}）</span>
                </div>

                <div class="review-remark-area" style="margin-top: 12px;">
                  <div class="remark-label">审核说明</div>
                  <div class="value-text">{{ getReviewRemarkDisplay(item) }}</div>
                </div>

                <!-- 审核后选项（已审核时显示） -->
                <div class="answer-section">
                  <div class="section-header" style="margin-top: 12px;">
                    <i class="el-icon-refresh"></i>
                    <span>审核后选项</span>
                    <el-tag v-if="hasOptionsChanged(item)" type="warning" size="mini" style="margin-left: 8px;">已修改</el-tag>
                  </div>
                  <div class="answer-options review-after-options" v-if="getSelectedOptionsWithLabels(item).length > 0">
                    <div 
                      v-for="(opt, optIdx) in getSelectedOptionsWithLabels(item)" 
                      :key="'cur-' + optIdx"
                      class="answer-option-item"
                      :class="{ changed: hasOptionsChanged(item) }"
                    >
                      <span class="option-text" v-html="opt.label"></span>
                      <el-tag v-if="opt.score > 0" type="success" size="mini">+{{ opt.score }}分</el-tag>
                    </div>
                  </div>
                  <div class="option-hint" v-else>
                    <i class="el-icon-info"></i> 暂无导入审核后的答案数据
                  </div>
                </div>

                <!-- 展示审核佐证材料 -->
                <div class="review-evidence-display" v-if="item.reviewEvidenceUrls">
                  <div class="section-header" style="margin-top: 8px;">
                    <i class="el-icon-folder-checked"></i>
                    <span>审核佐证</span>
                  </div>
                  <div class="evidence-cards" v-viewer="{movable: false}">
                    <template v-for="(file, idx) in parseEvidenceUrls(item.reviewEvidenceUrls)">
                      <div v-if="isImage(file)" :key="'rev-'+idx" class="evidence-card" @click.stop>
                        <div class="evidence-card-icon image"><i class="el-icon-picture-outline"></i></div>
                        <div class="evidence-card-info"><div class="evidence-card-name">{{ file.name || '图片' }}</div></div>
                        <img :src="file.url" style="display:none" />
                      </div>
                      <div v-else :key="'revf-'+idx" class="evidence-card" @click="previewFile(file)">
                        <div class="evidence-card-icon doc"><i class="el-icon-document"></i></div>
                        <div class="evidence-card-info"><div class="evidence-card-name">{{ file.name || '附件' }}</div></div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-tip">
          <p v-if="!currentRecord">请从左侧选择一个店铺进行审核</p>
          <p v-else>该店铺暂无申诉记录</p>
        </div>
      </div>

      <!-- 右侧：概览与导航 -->
      <div class="right-panel">
        <div class="score-summary">
          <span class="label">审核后总分</span>
          <span class="score">{{ reviewedTotalScore }}</span>
        </div>
        
        <!-- 题目导航 -->
        <div class="nav-title">题目导航</div>
        <div class="question-nav-list">
          <div
            v-for="(item, i) in currentItems"
            :key="item.id"
            class="nav-list-item"
            :class="getNavItemClass(item)"
            @click="scrollToItem(i)"
          >
            <span class="nav-item-label">{{ item.serialNo || (i + 1) }}. {{ truncateLabel(item.itemLabel, 12) }}</span>
            <el-tag :type="getItemStatusType(item.reviewStatus)" size="mini" class="nav-item-status">
              {{ getNavStatusText(item.reviewStatus) }}
            </el-tag>
          </div>
        </div>
        
        <!-- 切换按钮 -->
        <div class="nav-buttons">
          <el-button size="small" :disabled="currentIndex <= 0" @click="prevRecord">上一份</el-button>
          <el-button size="small" :disabled="currentIndex >= filteredRecords.length - 1" @click="nextRecord">下一份</el-button>
        </div>
      </div>
    </div>
    

  </div>
</template>

<script>
import axios from 'index/axios'

export default {
  name: 'AppealReview',
  props: {
    batchId: {
      type: [String, Number],
      required: true
    },
    initialRecordId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      batchName: '',
      records: [],
      currentRecord: null,
      // 当前选中记录的门店级证据（独立于 records/currentRecord，避免污染列表状态）
      currentDealerLevelEvidence: [],
      currentItems: [],
      currentIndex: -1,
      searchKeyword: '',
      statusFilter: '',
      answerTotalScore: 0,  // 答卷原始总分
      uploadUrl: '/api/Appeal/upload/image'  // 审核佐证上传地址
    }
  },
  computed: {
    filteredRecords() {
      let list = this.records
      
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase()
        list = list.filter(r => 
          (r.dealerCode || '').toLowerCase().includes(kw) ||
          (r.dealerName || '').toLowerCase().includes(kw)
        )
      }
      
      if (this.statusFilter === 'pending') {
        list = list.filter(r => r.status === 1 || r.status === 2)
      } else if (this.statusFilter === 'completed') {
        list = list.filter(r => r.status === 3) // 已完成
      }
      
      return list
    },
    totalCount() {
      return this.records.length
    },
    pendingCount() {
      return this.records.filter(r => r.status === 1 || r.status === 2).length
    },
    completedCount() {
      return this.records.filter(r => r.status === 3).length
    },
    originalTotalScore() {
      // 使用从 API 获取的答卷总分
      return this.answerTotalScore
    },
    reviewedTotalScore() {
      // 答卷总分 + 还分（只有状态3"还分处理"才加分）
      let restored = 0
      this.currentItems.forEach(item => {
        if (item.reviewStatus === 3 && item.restoredScore) {
          restored += item.restoredScore
        }
      })
      return this.answerTotalScore + restored
    },
    scoreChanged() {
      return this.reviewedTotalScore !== this.answerTotalScore
    },
    canCompleteReview() {
      return false // 不再使用
    }
  },
  created() {
    this.loadBatchInfo()
    this.loadRecords()
  },
  methods: {
    async loadBatchInfo() {
      try {
        // 使用 batch/list API 获取批次信息
        const res = await axios.post('/Appeal/batch/list', {
          page: 1,
          pageSize: 100
        })
        const data = res?.data?.data || res?.data || {}
        const batches = data.records || []
        
        // 查找当前批次
        const currentBatch = batches.find(b => String(b.id) === String(this.batchId))
        if (currentBatch) {
          this.batchName = currentBatch.batchName || currentBatch.batchNo || '未命名批次'
        } else {
          this.batchName = '批次 #' + this.batchId
        }
      } catch (e) {
        console.error('加载批次信息失败', e)
        this.batchName = '批次 #' + this.batchId
      }
    },
    
    async loadRecords() {
      this.loading = true
      try {
        const res = await axios.post('/Appeal/record/list', {
          batchId: this.batchId,
          page: 1,
          pageSize: 1000
        })
        const data = res?.data?.data || res?.data || {}
        this.records = (data.records || []).filter(r => r.status >= 1)
        
        if (this.records.length > 0) {
          const preferredRecord = this.initialRecordId
            ? this.records.find(r => String(r.id) === String(this.initialRecordId))
            : null
          const savedRecordId = this.getLastReviewPosition()
          const savedRecord = savedRecordId ? this.records.find(r => r.id == savedRecordId) : null
          const targetRecord = preferredRecord || savedRecord || this.records[0]
          
          if (targetRecord) {
            this.selectRecord(targetRecord)
          }
        } else {
          this.currentRecord = null
          this.currentItems = []
          this.currentIndex = -1
        }
      } catch (e) {
        console.error('Load appeal records failed', e)
      } finally {
        this.loading = false
      }
    },
    
    // ????????????
    getLastReviewPosition() {
      try {
        const key = `appeal_review_position_${this.batchId}`
        return localStorage.getItem(key)
      } catch {
        return null
      }
    },
    
    // 保存当前审核位置
    saveReviewPosition(recordId) {
      try {
        const key = `appeal_review_position_${this.batchId}`
        localStorage.setItem(key, recordId)
      } catch (e) {
        console.warn('Failed to save review position', e)
      }
    },
    
    filterRecords() {
      // 触发计算属性刷新
    },
    
    async selectRecord(record) {
      this.currentRecord = record
      this.currentIndex = this.filteredRecords.findIndex(r => r.id === record.id)
      this.currentDealerLevelEvidence = [] // 切换门店时先清空，等 detail 接口返回再填
      
      // 保存当前位置
      this.saveReviewPosition(record.id)
      
      try {
        const res = await axios.get(`/Appeal/record/detail/${record.id}`)
        const data = res?.data?.data || res?.data || {}

        // 保存答卷总分
        this.answerTotalScore = data.answerTotalScore || 0

        // 解析门店级证据（从阶段 1/2/4 引入，挂门店）
        // 存在独立变量里，避免污染 currentRecord/records 列表
        const rec = data.record || {}
        const dlJson = rec.dealerLevelEvidence || rec.DealerLevelEvidence
        let dealerLevelEvidence = []
        if (dlJson) {
          try {
            const parsed = typeof dlJson === 'string' ? JSON.parse(dlJson) : dlJson
            if (Array.isArray(parsed)) dealerLevelEvidence = parsed
          } catch { /* 格式异常则当空 */ }
        }
        this.currentDealerLevelEvidence = dealerLevelEvidence
        
        this.currentItems = (data.items || [])
          .filter(item => item.appealRemark || (item.evidenceUrls && item.evidenceUrls !== '[]'))
          .map(item => ({
          ...item,
          originalAnswer: item.originalAnswer || item.original_answer || item.originalAnswerJson || item.original_answer_json || item.originalOptions || item.original_options || item.originalOptionValues || item.original_option_values || item.originalOptionKeys || item.original_option_keys || item.optionsJson || item.OptionsJson || null,
          originalReviewRemark: item.originalReviewRemark || item.original_review_remark || item.auditRemark || item.audit_remark || item.originalReviewDesc || item.original_review_desc || item.originalReviewReason || item.original_review_reason || '',
          reviewRemarkInput: item.reviewRemark || '',
          restoredScoreInput: item.restoredScore || 0,
          pendingStatus: null,
          selectedOptionsInput: [],
          reviewEvidenceFileList: [],
          reviewEvidenceUrlsInput: []
        }))
      } catch (e) {
        console.error('加载详情失败', e)
        this.currentItems = []
      }
    },
    
    prevRecord() {
      if (this.currentIndex > 0) {
        this.selectRecord(this.filteredRecords[this.currentIndex - 1])
      }
    },
    
    nextRecord() {
      if (this.currentIndex < this.filteredRecords.length - 1) {
        this.selectRecord(this.filteredRecords[this.currentIndex + 1])
      }
    },
    
    setItemStatus(item, status) {
      this.$set(item, 'pendingStatus', status)
    },
    
    // 获取可选择的选项（根据审核状态过滤）
    getSelectableOptions(item, status) {
      if (!item.allOptions || !item.allOptions.length) return []
      if (status === 2) {
        // 修改失分原因：只能选非答案选项
        return item.allOptions.filter(opt => !opt.isAnswer)
      } else if (status === 3) {
        // 还分处理：只能选答案选项或有分值的选项
        return item.allOptions.filter(opt => opt.isAnswer || opt.hasScore)
      }
      return item.allOptions
    },
    
    // 根据选中的选项计算还分
    calcRestoredScore(item) {
      const selectedValues = item.selectedOptionsInput || []
      const options = item.allOptions || []
      let totalScore = 0
      
      for (const opt of options) {
        if (selectedValues.includes(opt.value)) {
          totalScore += opt.score || 0
        }
      }
      
      this.$set(item, 'restoredScoreInput', totalScore)
    },
    
    async submitItemReview(item) {
      if (!item.pendingStatus) {
        this.$message.warning('请先选择审核结果')
        return
      }
      
      if (item.pendingStatus === 3 && (!item.selectedOptionsInput || item.selectedOptionsInput.length === 0)) {
        this.$message.warning('请选择还分选项')
        return
      }
      
      try {
        const res = await axios.post('/Appeal/item/review', {
          itemId: item.id,
          reviewStatus: item.pendingStatus,
          reviewRemark: item.reviewRemarkInput,
          restoredScore: item.pendingStatus === 3 ? item.restoredScoreInput : 0,
          selectedOptions: (item.pendingStatus === 2 || item.pendingStatus === 3) 
            ? JSON.stringify(item.selectedOptionsInput) 
            : null,
          reviewEvidenceUrls: item.reviewEvidenceUrlsInput && item.reviewEvidenceUrlsInput.length > 0
            ? JSON.stringify(item.reviewEvidenceUrlsInput)
            : null
        })
        const data = res?.data?.data || res?.data || res || {}
        
        this.$set(item, 'reviewStatus', item.pendingStatus)
        this.$set(item, 'reviewRemark', item.reviewRemarkInput)
        this.$set(item, 'restoredScore', item.pendingStatus === 3 ? item.restoredScoreInput : 0)
        if ((item.pendingStatus === 2 || item.pendingStatus === 3) && item.selectedOptionsInput && item.selectedOptionsInput.length > 0) {
          this.$set(item, 'reviewAnswer', JSON.stringify(item.selectedOptionsInput))
        }
        if (item.reviewEvidenceUrlsInput && item.reviewEvidenceUrlsInput.length > 0) {
          this.$set(item, 'reviewEvidenceUrls', JSON.stringify(item.reviewEvidenceUrlsInput))
        }
        
        const nextRecordStatus = Number(data.recordStatus || 0)
        if (nextRecordStatus && this.currentRecord) {
          this.$set(this.currentRecord, 'status', nextRecordStatus)
          const recordIndex = this.records.findIndex(r => r.id === this.currentRecord.id)
          if (recordIndex > -1) {
            this.$set(this.records, recordIndex, {
              ...this.records[recordIndex],
              status: nextRecordStatus
            })
          }
        }
        
        const statusMap = { 1: '维持原判', 2: '修改失分原因', 3: '还分处理', 4: '不涉及' }
        this.$message.success(`已提交：${statusMap[item.pendingStatus]}`)
      } catch (e) {
        console.error('Submit appeal review failed', e)
        this.$message.error('提交失败')
      }
    },
    
    // ???????????????
    onReviewEvidenceUploaded(res, file, fileList, item) {
      const data = res?.data || res
      if (data?.url) {
        if (!item.reviewEvidenceUrlsInput) {
          this.$set(item, 'reviewEvidenceUrlsInput', [])
        }
        item.reviewEvidenceUrlsInput.push({
          url: data.url,
          name: file.name
        })
      }
    },
    
    // 审核佐证移除回调
    onReviewEvidenceRemoved(file, fileList, item) {
      const url = file?.response?.data?.url || file?.url
      if (url && item.reviewEvidenceUrlsInput) {
        item.reviewEvidenceUrlsInput = item.reviewEvidenceUrlsInput.filter(f => f.url !== url)
      }
    },
    
    scrollToItem(index) {
      const refKey = 'item-' + index
      const ref = this.$refs[refKey]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else if (ref) {
        // 如果 ref 不是数组
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    
    goBack() {
      this.$emit('back')
    },
    
    // 辅助方法
    formatTime(timeStr) {
      if (!timeStr) return '-'
      return new Date(timeStr).toLocaleString('zh-CN')
    },
    
    getRecordStatusClass(record) {
      if (record.status === 3) return 'completed'
      if (record.status === 2) return 'processing'
      if (record.status === 1) return 'pending'
      return 'draft'
    },
    
    getRecordStatusText(record) {
      if (record.status === 3) return '已完成'
      if (record.status === 2) return '审核中'
      if (record.status === 1) return '待审核'
      return '草稿'
    },
    
    getItemStatusType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'danger'
      return 'info'
    },
    
    getItemStatusText(status) {
      const statusMap = {
        0: '待审核',
        1: '维持原判',
        2: '修改失分原因',
        3: '还分处理',
        4: '不涉及'
      }
      return statusMap[status] || '待审核'
    },

    // 审核说明：优先取一审/二审/三审失分备注
    getReviewRemarkDisplay(item) {
      if (!item) return '-'
      const candidates = [
        item.firstLoseReason,
        item.first_lose_reason,
        item.firstLoseRemark,
        item.first_lose_remark,
        item.secondLoseReason,
        item.second_lose_reason,
        item.secondLoseRemark,
        item.second_lose_remark,
        item.thirdLoseReason,
        item.third_lose_reason,
        item.thirdLoseRemark,
        item.third_lose_remark,
        item.auditRemark,
        item.audit_remark,
        item.reviewRemark
      ]
      for (const val of candidates) {
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          return String(val)
        }
      }
      return '-'
    },
    
    getNavItemClass(item) {
      if (item.reviewStatus === 3) return 'restored' // 还分
      if (item.reviewStatus === 4) return 'not-applicable' // 不涉及
      if (item.reviewStatus >= 1) return 'reviewed' // 已处理
      return 'pending'
    },
    
    // 获取审核状态对应的样式类
    getReviewStatusStyle(status) {
      const styleMap = {
        1: 'info',      // 维持原判
        2: 'warning',   // 修改失分原因
        3: 'success',   // 还分处理
        4: 'info'       // 不涉及
      }
      return styleMap[status] || 'info'
    },
    
    // 获取短编码（取最后一段或截取后8位）
    getShortCode(dealerCode) {
      if (!dealerCode) return ''
      const parts = dealerCode.split(/[-_]/)
      if (parts.length > 1) {
        return parts[parts.length - 1]
      }
      if (dealerCode.length > 12) {
        return '...' + dealerCode.slice(-8)
      }
      return dealerCode
    },
    
    // 获取审核后修改的选项并匹配真实标签
    getSelectedOptionsWithLabels(item) {
      const allOptions = item.allOptions || []
      const selectedValues = this._parseOptionValues(this._getModifiedOptionsSource(item))

      // 从 allOptions 中查找匹配的选项以获取真实标签
      const result = []
      for (const val of selectedValues) {
        const matchedOpt = allOptions.find(opt =>
          String(opt.value) === val ||
          String(opt.option_key) === val ||
          opt.label === val
        )

        if (matchedOpt) {
          result.push({
            label: matchedOpt.label || matchedOpt.text || val,
            value: val,
            score: matchedOpt.score || 0,
            selected: true
          })
        } else {
          result.push({
            label: val,
            value: val,
            score: 0,
            selected: true
          })
        }
      }

      return result
    },
    
    // 解析选项值为字符串数组（通用，兼容字符串/数组/对象）
    _parseOptionValues(rawValue) {
      if (!rawValue) return []

      if (Array.isArray(rawValue)) {
        return rawValue.map(v => {
          if (typeof v === 'object' && v !== null) {
            if (v.value !== undefined) return String(v.value)
            if (v.option_key !== undefined) return String(v.option_key)
            if (v.label !== undefined) return String(v.label)
            if (v.id !== undefined) return String(v.id)
          }
          return String(v)
        })
      }

      if (typeof rawValue === 'object') {
        const arrayCandidates = [
          rawValue.values,
          rawValue.options,
          rawValue.optionValues,
          rawValue.option_values,
          rawValue.optionKeys,
          rawValue.option_keys,
          rawValue.selectedOptions,
          rawValue.selected_options,
          rawValue.reviewOptions,
          rawValue.review_options
        ]
        for (const candidate of arrayCandidates) {
          if (Array.isArray(candidate) && candidate.length > 0) {
            return this._parseOptionValues(candidate)
          }
        }

        if (rawValue.value !== undefined) return [String(rawValue.value)]
        if (rawValue.option_key !== undefined) return [String(rawValue.option_key)]
        if (rawValue.label !== undefined) return [String(rawValue.label)]
        if (rawValue.id !== undefined) return [String(rawValue.id)]
      }

      const jsonStr = String(rawValue)
      try {
        const parsed = JSON.parse(jsonStr)
        if (Array.isArray(parsed)) {
          return this._parseOptionValues(parsed)
        }
        if (typeof parsed === 'object' && parsed) {
          return this._parseOptionValues(parsed)
        }
        return [String(parsed)]
      } catch {
        if (jsonStr.includes(',')) {
          return jsonStr.split(',').map(v => v.trim()).filter(Boolean)
        }
        return [jsonStr]
      }
    },

    // 获取原始选项（优先用快照 originalAnswer，否则用当前 optionsJson）
    getOriginalOrCurrentOptions(item) {
      const allOptions = item.allOptions || []
      const source = item.originalAnswer || item.optionsJson
      const selectedValues = this._parseOptionValues(source)
      
      return selectedValues.map(val => {
        const matched = allOptions.find(opt => 
          String(opt.value) === val || 
          String(opt.option_key) === val ||
          opt.label === val
        )
        return {
          label: matched ? (matched.label || matched.text || val) : val,
          value: val,
          score: matched ? (matched.score || 0) : 0,
          selected: true
        }
      })
    },

    // 获取“导入审核之后的答案”数据源（仅审核结果字段，不回退原始字段）
    _getModifiedOptionsSource(item) {
      const candidates = [
        item.selectedOptions,
        item.selected_options,
        item.selectedOption,
        item.selected_option,
        item.selectedOptionList,
        item.selected_option_list,
        item.selectedOptionValues,
        item.selected_option_values,
        item.selectedOptionKeys,
        item.selected_option_keys,
        item.reviewAnswer,
        item.ReviewAnswer,
        item.review_answer,
        item.reviewOptions,
        item.review_options,
        item.reviewOption,
        item.review_option,
        item.reviewOptionList,
        item.review_option_list,
        item.reviewOptionValues,
        item.review_option_values,
        item.reviewOptionKeys,
        item.review_option_keys,
        item.reviewResultOptions,
        item.review_result_options,
        item.reviewedOptions,
        item.reviewed_options,
        item.finalOptions,
        item.final_options,
        item.finalAnswer,
        item.final_answer,
        item.afterReviewOptions,
        item.after_review_options,
        item.importReviewOptions,
        item.import_review_options,
        item.reviewResult,
        item.review_result,
        item.reviewData,
        item.review_data,
        // 兜底：部分导入流程会直接覆盖 optionsJson 作为“审核后选项”
        item.optionsJson,
        item.OptionsJson,
        item.options_json
      ]

      for (const value of candidates) {
        if (Array.isArray(value) && value.length > 0) return value
        if (value !== undefined && value !== null && value !== '') return value
      }

      return null
    },

    // 判断选项是否在审核后被修改（reviewStatus 为修改/还分时显示）
    hasOptionsChanged(item) {
      if (item.reviewStatus !== 2 && item.reviewStatus !== 3) return false
      const modifiedSource = this._getModifiedOptionsSource(item)
      if (!modifiedSource) return false
      // 获取“审核判定选项”展示的数据源（和 getOriginalOrCurrentOptions 一致）
      const displaySource = item.originalAnswer || item.optionsJson
      if (!displaySource) return true // 没有对照组，直接显示
      // 对比两个区块的选项值，如果一样就不重复展示
      const displayVals = this._parseOptionValues(displaySource).sort().join(',')
      const modifiedVals = this._parseOptionValues(modifiedSource).sort().join(',')
      return displayVals !== modifiedVals
    },
    
    // 解析选项 JSON（与店端 appealNew.vue 一致）
    parseOptions(optionsJson) {
      if (!optionsJson) return []
      try {
        const parsed = JSON.parse(optionsJson)
        // 如果是数组，直接返回；如果是对象/值，包装成数组
        if (Array.isArray(parsed)) {
          return parsed.map(opt => ({
            label: opt.label || opt.text || opt.value || (typeof opt === 'string' ? opt : String(opt)),
            value: opt.value || opt,
            selected: true
          }))
        }
        // 单个值的情况
        return [{ label: String(parsed), value: parsed, selected: true }]
      } catch {
        // 非 JSON 格式，可能是逗号分隔的值
        if (optionsJson.includes(',')) {
          return optionsJson.split(',').filter(v => v.trim()).map(v => ({
            label: v.trim(),
            value: v.trim(),
            selected: true
          }))
        }
        return [{ label: optionsJson, value: optionsJson, selected: true }]
      }
    },
    
    parseEvidenceUrls(urlsStr) {
      if (!urlsStr) return []
      try {
        const parsed = JSON.parse(urlsStr)
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            if (typeof item === 'object' && item.url) return item
            return { url: item, name: '' }
          })
        }
        return []
      } catch {
        return urlsStr.split(',').filter(u => u).map(url => ({ url, name: '' }))
      }
    },
    
    isImage(file) {
      const url = typeof file === 'object' ? file.url : file
      if (!url) return false
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || url.includes('/image/')
    },
    
    isVideo(file) {
      const url = typeof file === 'object' ? file.url : file
      const name = typeof file === 'object' ? (file.name || '') : ''
      if (!url && !name) return false
      return /\.(mp4|mov|avi|wmv|flv|mkv|webm|3gp|m4v)$/i.test(name || url)
    },

    // --- 门店级证据辅助 ---
    openDealerLevelEvidence(ev) {
      if (!ev || !ev.url) return
      // 视频/音频用弹窗播放，其它新标签页打开
      if (ev.fileType === 'video' || ev.fileType === 'audio') {
        this.previewFile({ url: ev.url, name: ev.displayName })
      } else {
        window.open(ev.url, '_blank')
      }
    },
    dealerEvIconClass(fileType) {
      switch (fileType) {
        case 'image': return 'el-icon-picture-outline'
        case 'audio': return 'el-icon-headset'
        case 'video': return 'el-icon-video-camera'
        case 'signature': return 'el-icon-edit-outline'
        default: return 'el-icon-document'
      }
    },
    dealerEvIconColor(fileType) {
      switch (fileType) {
        case 'image': return '#8b5cf6'
        case 'audio': return '#22c55e'
        case 'video': return '#f59e0b'
        case 'signature': return '#ec4899'
        default: return '#3b82f6'
      }
    },
    
    previewFile(file) {
      const url = typeof file === 'object' ? file.url : file
      if (!url) return
      if (this.isVideo(file)) {
        // 视频：用弹窗播放
        this.$msgbox({
          title: file.name || '视频预览',
          message: this.$createElement('video', {
            attrs: { src: url, controls: true, autoplay: true, style: 'width:100%;max-height:70vh;' }
          }),
          showConfirmButton: false,
          customClass: 'video-preview-dialog',
          beforeClose: (action, instance, done) => done()
        }).catch(() => {})
      } else {
        // 其他文件：新窗口打开
        window.open(url, '_blank')
      }
    },
    
    truncateLabel(label, maxLen) {
      if (!label) return ''
      // 去掉 HTML 标签
      const text = label.replace(/<[^>]*>/g, '')
      return text.length > maxLen ? text.substring(0, maxLen) + '...' : text
    },
    
    getNavStatusText(status) {
      const map = { 0: '待审', 1: '维持', 2: '改因', 3: '还分', 4: '不涉及' }
      return map[status] || '待审'
    }
  }
}
</script>

<style lang="scss" scoped>
.appeal-review-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
}

.review-header {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .title {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }
  
  .header-center {
    .stats {
      color: #666;
      font-size: 14px;
      
      strong {
        color: #1890ff;
        margin: 0 2px;
      }
    }
  }
}

.review-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  width: 280px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  .panel-header {
    padding: 20px 20px 16px;
    border-bottom: 1px solid #f0f0f0;
    
    .panel-title {
      font-weight: 700;
      font-size: 16px;
      color: #1a1a1a;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      font-size: 13px;
      color: #999;
      
      strong {
        color: #1890ff;
      }
    }
  }
  
  .search-box {
    padding: 12px 16px;
  }
  
  .record-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
    
    .record-item {
      padding: 14px 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 12px;
      transition: all 0.15s;
      border: none !important;
      
      &:hover {
        background: #fafafa;
      }
      
      &.is-current {
        background: #e6f7ff;
        box-shadow: inset 3px 0 0 #1890ff;
        border: none !important;
        
        .record-icon {
          background: #1890ff;
          color: #fff;
        }
      }
      
      .record-icon {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: #e8edf2;
        color: #8c9aac;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        flex-shrink: 0;
        transition: all 0.15s;
      }
      
      .record-info-main {
        flex: 1;
        min-width: 0;
        
        .record-name {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 2px;
        }
        
        .record-code {
          font-size: 12px;
          color: #999;
        }
      }
      
      .status-badge {
        font-size: 11px;
        padding: 2px 10px;
        border-radius: 10px;
        font-weight: 500;
        flex-shrink: 0;
        
        &.pending {
          background: #e6f7ff;
          color: #1890ff;
        }
        
        &.completed {
          background: #f6ffed;
          color: #52c41a;
        }
      }
    }
  }
  
  .filter-option {
    padding: 10px 16px;
    border-top: 1px solid #e8e8e8;
  }
}

.center-panel {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
  
  .appeal-list {
    max-width: 860px;
    margin: 0 auto;
  }
  
  .appeal-card {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 28px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    
    .card-header {
      padding: 20px 28px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      border-bottom: 1px solid #f0f0f0;
      background: #fafbfc;
      
      .card-title-row {
        flex: 1;
        
        .item-serial {
          color: #1890ff;
          font-weight: 700;
          font-size: 16px;
          display: inline;
        }
        
        .item-title-text {
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1a;
          line-height: 1.5;
          display: inline;
          
          ::v-deep p,
          ::v-deep div {
            display: inline;
            margin: 0;
          }
        }
      }
    }
    
    .card-body {
      padding: 0; // 由 process-section 内部控制
      
      // ========= 流程区块（三大块的统一样式） =========
      .process-section {
        padding: 24px 28px;
        
        & + .process-section {
          border-top: 1px solid #f0f0f0;
        }
        
        .process-section-title {
          font-size: 14px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 20px;
          padding-left: 10px;
          border-left: 3px solid #1890ff;
          line-height: 1;
        }
      }
      
      // ========= 通用 section-header =========
      .section-header {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 14px;
        
        i {
          color: #1890ff;
          font-size: 16px;
        }
      }
      
      // ========= 通用 审核备注 / 值展示 =========
      .review-remark-area {
        margin-bottom: 16px;
        
        .remark-label {
          color: #666;
          font-size: 13px;
          margin-bottom: 8px;
          font-weight: 500;
        }
        
        .value-text {
          color: #555;
          font-size: 14px;
          line-height: 1.6;
          padding: 10px 14px;
          background: #fafafa;
          border-radius: 6px;
        }
      }
      
      // ========= 通用提示 =========
      .option-hint {
        color: #bbb;
        font-size: 13px;
        padding: 12px 16px;
        background: #fafafa;
        border-radius: 6px;
        
        i { margin-right: 4px; }
      }
      
      // ========= 商户申诉陈述 =========
      .appeal-statement-section {
        margin-bottom: 20px;
        
        .statement-quote {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 20px 24px;
          position: relative;
          
          .quote-mark {
            font-size: 48px;
            line-height: 1;
            color: #d9d9d9;
            font-family: Georgia, serif;
            margin-bottom: -8px;
          }
          
          .quote-text {
            color: #555;
            font-size: 14px;
            line-height: 1.8;
          }
        }
      }
      
      // ========= 答题选项 =========
      .answer-section {
        margin-bottom: 20px;
        
        .answer-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .answer-option-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: #fafafa;
          border-radius: 8px;
          border: 1px solid #f0f0f0;
          
          .option-text {
            flex: 1;
            color: #555;
            font-size: 14px;
          }
          
          &.changed {
            background: #f6ffed;
            border-color: #b7eb8f;
            border-left: 3px solid #52c41a;
          }
        }
        
        // 审核后选项区域的独立样式
        &.review-after-section {
          .section-header {
            margin-top: 4px;
          }
        }
      }
      
      // ========= 佐证材料 =========
      .evidence-section {
        margin-bottom: 20px;
        
        .evidence-cards {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .evidence-card {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: #f8f9fa;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s;
          min-width: 160px;
          border: 1px solid #f0f0f0;
          
          &:hover {
            background: #eef1f5;
            border-color: #d9d9d9;
          }
          
          .evidence-card-icon {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            flex-shrink: 0;
            
            &.image {
              background: #e6f7ff;
              color: #1890ff;
            }
            
            &.video {
              background: #fff1f0;
              color: #f5222d;
            }
            
            &.doc {
              background: #e6fffb;
              color: #13c2c2;
            }
          }
          
          .evidence-card-info {
            flex: 1;
            min-width: 0;
            
            .evidence-card-name {
              font-size: 13px;
              color: #333;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
      }
      
      // ========= 审核裁定（待审核时） =========
      .review-action-section {
        .verdict-cards {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          
          .verdict-card {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 20px 12px;
            border: 1px solid #e8e8e8;
            border-radius: 10px;
            cursor: pointer;
            transition: all 0.2s;
            background: #fff;
            
            i {
              font-size: 22px;
              color: #999;
            }
            
            span {
              font-size: 12px;
              color: #666;
              white-space: nowrap;
            }
            
            &:hover {
              border-color: #1890ff;
              background: #f0f7ff;
            }
            
            &.selected {
              border-color: #1890ff;
              background: #e6f7ff;
              
              i { color: #1890ff; }
              span { color: #1890ff; font-weight: 600; }
            }
          }
        }
        
        // 选项勾选区域
        .option-select-area {
          margin-bottom: 20px;
          
          .option-select-label {
            color: #666;
            font-size: 13px;
            margin-bottom: 10px;
            font-weight: 500;
          }
          
          .option-checkboxes {
            .el-checkbox-group {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }
            
            .option-checkbox {
              display: flex;
              align-items: center;
              padding: 12px 14px;
              background: #fafafa;
              border-radius: 8px;
              border: 1px solid #f0f0f0;
              margin: 0;
              transition: all 0.15s;
              
              &.is-checked {
                background: #e6f7ff;
                border-color: #91d5ff;
              }
              
              &.scored.is-checked {
                background: #f6ffed;
                border-color: #b7eb8f;
              }
              
              .el-checkbox__label {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
              }
            }
          }
        }
        
        // 还分行
        .restored-score-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding: 14px 16px;
          background: #f6ffed;
          border-radius: 8px;
          border: 1px solid #d9f7be;
          
          .restored-label {
            color: #52c41a;
            font-size: 14px;
            font-weight: 600;
          }
          
          .score-hint {
            color: #999;
            font-size: 13px;
          }
        }
        
        // 审核佐证上传
        .review-evidence-area {
          margin-bottom: 20px;
        }
        
        // 提交按钮
        .submit-row {
          display: flex;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #f0f0f0;
          
          .submit-btn {
            min-width: 200px;
            height: 42px;
            font-size: 15px;
            border-radius: 21px;
            letter-spacing: 2px;
            background: #1890ff;
            border-color: #1890ff;
          }
        }
      }
      
      // ========= 已审核结果 =========
      .reviewed-result {
        .result-card {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 20px;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 500;
          margin-bottom: 20px;
          
          i { font-size: 18px; }
          
          &.info {
            background: #fafafa;
            color: #8c8c8c;
            border: 1px solid #f0f0f0;
          }
          
          &.success {
            background: #f6ffed;
            color: #52c41a;
            border: 1px solid #d9f7be;
          }
          
          &.warning {
            background: #fffbe6;
            color: #faad14;
            border: 1px solid #ffe58f;
          }
          
          .restored-score {
            color: #52c41a;
            font-weight: 700;
          }
        }
        
        // 审核后选项
        .answer-section {
          margin-top: 16px;
          margin-bottom: 16px;
        }
        
        // 审核佐证展示
        .review-evidence-display {
          margin-top: 16px;
        }
      }
    }
  }
  
  .empty-tip {
    text-align: center;
    padding: 120px 0;
    color: #bbb;
    font-size: 14px;
  }
}

.right-panel {
  width: 220px;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  .score-summary {
    text-align: center;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f0f0f0;
    
    .label {
      display: block;
      color: #999;
      font-size: 12px;
      margin-bottom: 8px;
    }
    
    .score {
      font-size: 40px;
      font-weight: 700;
      color: #f5222d;
      line-height: 1;
      
      &.changed {
        color: #52c41a;
      }
    }
    
    &.review-score .score {
      color: #1890ff;
    }
  }
  
  .nav-title {
    color: #999;
    font-size: 12px;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .question-nav-list {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 16px;
    
    .nav-list-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 10px;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s;
      margin-bottom: 4px;
      
      &:hover {
        background: #f5f5f5;
      }
      
      &.pending {
        .nav-item-label { color: #999; }
      }
      
      &.reviewed {
        background: #e6f7ff;
        .nav-item-label { color: #1890ff; }
      }
      
      &.restored {
        background: #f6ffed;
        .nav-item-label { color: #52c41a; }
      }
      
      &.not-applicable {
        .nav-item-label { color: #999; }
      }
      
      .nav-item-label {
        font-size: 12px;
        color: #333;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-right: 6px;
      }
      
      .nav-item-status {
        flex-shrink: 0;
      }
    }
  }
  
  .nav-buttons {
    margin-top: auto;
    display: flex;
    flex-direction: row;
    gap: 8px;
    
    .el-button {
      flex: 1;
    }
  }
}

.review-footer {
  height: 56px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.03);
}

// 响应式布局 - 平板
@media screen and (max-width: 1200px) {
  .right-panel {
    width: 160px;
    padding: 16px;
  }
  .left-panel { width: 220px; }
}

// 响应式布局 - 小屏
@media screen and (max-width: 992px) {
  .right-panel { display: none; }
  .left-panel { width: 180px; }
  .center-panel {
    padding: 16px;
    .appeal-list { max-width: 100%; }
  }
}

// 响应式布局 - 移动端
@media screen and (max-width: 768px) {
  .review-content {
    flex-direction: column;
    .left-panel {
      width: 100%;
      max-height: 200px;
      border-right: none;
      border-bottom: 1px solid #e8e8e8;
    }
  }
  .review-header {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
  .review-footer {
    height: auto;
    padding: 12px;
    flex-wrap: wrap;
  }
}

/* 门店级证据卡片（审核端，在 center-panel 顶部）*/
.dealer-level-evidence-card {
  margin: 12px 16px 0;
  padding: 12px 16px;
  background: #fffaeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}
.dealer-level-evidence-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 10px;
  i { font-size: 16px; }
  .dealer-level-evidence-card-hint {
    margin-left: auto;
    font-size: 12px;
    font-weight: 400;
    color: #9a7110;
  }
}
.dealer-level-evidence-card-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 8px;
}
.dealer-level-evidence-card-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #fef3c7;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  i { font-size: 22px; flex-shrink: 0; }
  &:hover {
    border-color: #f59e0b;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
  }
}
.dealer-level-evidence-card-info { flex: 1; min-width: 0; }
.dealer-level-evidence-card-name {
  font-size: 12px;
  color: #1a1a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.dealer-level-evidence-card-meta {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}
</style>

<style lang="scss">
.video-preview-dialog {
  width: 720px !important;
  max-width: 90vw;
  
  .el-message-box__message {
    padding: 0;
    
    video {
      display: block;
      border-radius: 4px;
    }
  }
}

.appeal-review-container .record-list .record-item {
  border: none !important;
  outline: none !important;
  box-shadow: none;
  
  &.is-current {
    box-shadow: inset 3px 0 0 #1890ff;
  }
}
</style>
